import { FeatureFlagProvider } from '@anchorage/feature-flags';
import { handleError } from '@anchorage/sentry';
import { FEATURE_FLAGS_OPTIONS } from 'constants/featureFlags';
import type { JSX } from 'react';
import { useMemo } from 'react';

function FeatureFlagsProvider({
  children,
  trafficKey,
}: {
  children: JSX.Element;
  trafficKey?: string;
}) {
  const providerConfig = useMemo(() => {
    const hasSession = trafficKey && trafficKey !== 'NA';

    return {
      override: {
        storage: localStorage,
        storageKey: 'onboarding-feature-flags-overrides',
      },
      launchDarkly: {
        clientSideId: FEATURE_FLAGS_OPTIONS.options.clientSideId,
        context: hasSession
          ? {
              // Session-based context
              kind: 'user',
              key: trafficKey,
              session_id: trafficKey,
            }
          : {
              // Anonymous context (no session yet)
              kind: 'user',
              key: 'anonymous',
              anonymous: true,
            },
      },
    };
  }, [trafficKey]);

  // Create a key that changes when session changes to force provider remount
  // This ensures LaunchDarkly SDK reinitializes with new context on session change
  const providerKey = trafficKey || 'anonymous';

  // If no trafficKey, don't initialize providers yet
  if (!trafficKey) {
    return <>{children}</>;
  }

  return (
    <FeatureFlagProvider
      key={providerKey}
      config={providerConfig}
      defaults={FEATURE_FLAGS_OPTIONS.defaults}
      onError={handleError}
    >
      {children}
    </FeatureFlagProvider>
  );
}

export default FeatureFlagsProvider;
